import { STAT_CATEGORIES_ABBR } from '../constants/category-constants';
import { StatCategoryAbrv } from './category.types';

export const DefensiveGroup = {
  ScoringAllowed: 'ScoringAllowed',
  YardsAllowed: 'YardsAllowed',
  VsPass: 'VsPass',
  PositivePlays: 'PositivePlays',
  ThirdDownStand: '3rdDownStand',
  FourthDownStand: '4thDownStand',
  RedZoneStand: 'RedZoneStand',
} as const;

export const OffensiveGroup = {
  Totals: 'Totals',
  Scoring: 'Scoring',
  Passing: 'Passing',
  Rushing: 'Rushing',
  NegativePlays: 'NegativePlays',
  Turnovers: 'Turnovers',
  FirstDowns: '1stDowns',
  ThirdDowns: '3rdDowns',
  FourthDowns: '4thDowns',
  RedZone: 'RedZone',
} as const;

export type CategoryGroupType = typeof DefensiveGroup | typeof OffensiveGroup;

export type CategoryGroupName =
  | (typeof DefensiveGroup)[keyof typeof DefensiveGroup]
  | (typeof OffensiveGroup)[keyof typeof OffensiveGroup];

export interface ICategoryGroup {
  [groupName: string]: StatCategoryAbrv[];
}

export const CategoryRankingsKind = {
  Defense: 'Defense',
  Offense: 'Offense',
} as const;

export type CategoryRankingsKindType = (typeof CategoryRankingsKind)[keyof typeof CategoryRankingsKind];

export interface CategoryDefinition {
  abrv: StatCategoryAbrv;
  longName: string;
  label: string;
  description: string;
  group: CategoryGroupName;
  kind: CategoryRankingsKindType;
}

export interface GroupDefinition {
  id: CategoryGroupName;
  label: string;
  kind: CategoryRankingsKindType;
  categories: StatCategoryAbrv[];
}

export const CategoryDefinitions: Record<StatCategoryAbrv, CategoryDefinition> = {
  [STAT_CATEGORIES_ABBR.totalPointsAllowed]: {
    abrv: STAT_CATEGORIES_ABBR.totalPointsAllowed,
    longName: 'totalPointsAllowed',
    label: 'Points Allowed',
    description: 'Points Allowed',
    group: DefensiveGroup.ScoringAllowed,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalScoringTotalTDAgainst]: {
    abrv: STAT_CATEGORIES_ABBR.totalScoringTotalTDAgainst,
    longName: 'totalScoringTotalTDAgainst',
    label: 'Total TDs',
    description: 'Allowed Total TDs',
    group: DefensiveGroup.ScoringAllowed,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalScoringPassingTDAgainst]: {
    abrv: STAT_CATEGORIES_ABBR.totalScoringPassingTDAgainst,
    longName: 'totalScoringPassingTDAgainst',
    label: 'Pass TDs',
    description: 'Allowed Passing TDs',
    group: DefensiveGroup.ScoringAllowed,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalScoringRushingTDAgainst]: {
    abrv: STAT_CATEGORIES_ABBR.totalScoringRushingTDAgainst,
    longName: 'totalScoringRushingTDAgainst',
    label: 'Rush TDs',
    description: 'Allowed Rushing TDs',
    group: DefensiveGroup.ScoringAllowed,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalScoringFGAgainst]: {
    abrv: STAT_CATEGORIES_ABBR.totalScoringFGAgainst,
    longName: 'totalScoringFGAgainst',
    label: 'FGs',
    description: 'Allowed Field Goals',
    group: DefensiveGroup.ScoringAllowed,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalScoring2PtAgainst]: {
    abrv: STAT_CATEGORIES_ABBR.totalScoring2PtAgainst,
    longName: 'totalScoring2PtAgainst',
    label: '2PT',
    description: 'Allowed 2pt Conversions',
    group: DefensiveGroup.ScoringAllowed,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalScoringEPAgainst]: {
    abrv: STAT_CATEGORIES_ABBR.totalScoringEPAgainst,
    longName: 'totalScoringEPAgainst',
    label: 'PAT',
    description: 'Allowed Extra Points',
    group: DefensiveGroup.ScoringAllowed,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalYardsAllowed]: {
    abrv: STAT_CATEGORIES_ABBR.totalYardsAllowed,
    longName: 'totalYardsAllowed',
    label: 'Total',
    description: 'Total Yards Allowed',
    group: DefensiveGroup.YardsAllowed,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalPassingYardsAllowed]: {
    abrv: STAT_CATEGORIES_ABBR.totalPassingYardsAllowed,
    longName: 'totalPassingYardsAllowed',
    label: 'Pass',
    description: 'Passing Yards Allowed',
    group: DefensiveGroup.YardsAllowed,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalRushingYardsAllowed]: {
    abrv: STAT_CATEGORIES_ABBR.totalRushingYardsAllowed,
    longName: 'totalRushingYardsAllowed',
    label: 'Rush',
    description: 'Rushing Yards Allowed',
    group: DefensiveGroup.YardsAllowed,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalPassAttemptsAgainst]: {
    abrv: STAT_CATEGORIES_ABBR.totalPassAttemptsAgainst,
    longName: 'totalPassAttemptsAgainst',
    label: 'Attempts',
    description: 'Pass Attempts Against',
    group: DefensiveGroup.VsPass,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalPassIncompletions]: {
    abrv: STAT_CATEGORIES_ABBR.totalPassIncompletions,
    longName: 'totalPassIncompletions',
    label: 'Incompletions',
    description: 'Pass Incompletions',
    group: DefensiveGroup.VsPass,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalPassIncompletePercentage]: {
    abrv: STAT_CATEGORIES_ABBR.totalPassIncompletePercentage,
    longName: 'totalPassIncompletePercentage',
    label: 'Percentage',
    description: 'Pass Incomplete Percentage',
    group: DefensiveGroup.VsPass,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalForcedFumbles]: {
    abrv: STAT_CATEGORIES_ABBR.totalForcedFumbles,
    longName: 'totalForcedFumbles',
    label: 'Forced Fumbles',
    description: 'Forced Fumbles',
    group: DefensiveGroup.PositivePlays,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalFumblesRecovered]: {
    abrv: STAT_CATEGORIES_ABBR.totalFumblesRecovered,
    longName: 'totalFumblesRecovered',
    label: 'Fumbles Recovered',
    description: 'Fumbles Recovered',
    group: DefensiveGroup.PositivePlays,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalDefensiveInterceptions]: {
    abrv: STAT_CATEGORIES_ABBR.totalDefensiveInterceptions,
    longName: 'totalDefensiveInterceptions',
    label: 'Interceptions',
    description: 'Defensive Interceptions',
    group: DefensiveGroup.PositivePlays,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalSacks]: {
    abrv: STAT_CATEGORIES_ABBR.totalSacks,
    longName: 'totalSacks',
    label: 'Sacks',
    description: 'Sacks',
    group: DefensiveGroup.PositivePlays,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalSafeties]: {
    abrv: STAT_CATEGORIES_ABBR.totalSafeties,
    longName: 'totalSafeties',
    label: 'Safety',
    description: 'Safeties',
    group: DefensiveGroup.PositivePlays,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalDefensiveOrSpecialTeamsTds]: {
    abrv: STAT_CATEGORIES_ABBR.totalDefensiveOrSpecialTeamsTds,
    longName: 'totalDefensiveOrSpecialTeamsTds',
    label: 'Defensive TDs',
    description: 'Defensive TDs',
    group: DefensiveGroup.PositivePlays,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalThirdDownAttemptsAgainst]: {
    abrv: STAT_CATEGORIES_ABBR.totalThirdDownAttemptsAgainst,
    longName: 'totalThirdDownAttemptsAgainst',
    label: 'Attempts',
    description: 'Third Down Attempts Against',
    group: DefensiveGroup.ThirdDownStand,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalThirdDownStops]: {
    abrv: STAT_CATEGORIES_ABBR.totalThirdDownStops,
    longName: 'totalThirdDownStops',
    label: 'Stops',
    description: 'Third Down Stops',
    group: DefensiveGroup.ThirdDownStand,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalThirdDownStopEfficiency]: {
    abrv: STAT_CATEGORIES_ABBR.totalThirdDownStopEfficiency,
    longName: 'totalThirdDownStopEfficiency',
    label: 'Efficiency',
    description: 'Third Down Stop Efficiency',
    group: DefensiveGroup.ThirdDownStand,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalFourthDownAttemptsAgainst]: {
    abrv: STAT_CATEGORIES_ABBR.totalFourthDownAttemptsAgainst,
    longName: 'totalFourthDownAttemptsAgainst',
    label: 'Attempts',
    description: 'Fourth Down Attempts Against',
    group: DefensiveGroup.FourthDownStand,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalFourthDownStops]: {
    abrv: STAT_CATEGORIES_ABBR.totalFourthDownStops,
    longName: 'totalFourthDownStops',
    label: 'Stops',
    description: 'Fourth Down Stops',
    group: DefensiveGroup.FourthDownStand,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalFourthDownStopEfficiency]: {
    abrv: STAT_CATEGORIES_ABBR.totalFourthDownStopEfficiency,
    longName: 'totalFourthDownStopEfficiency',
    label: 'Efficiency',
    description: 'Fourth Down Stop Efficiency',
    group: DefensiveGroup.FourthDownStand,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalRedZoneAttemptsAgainst]: {
    abrv: STAT_CATEGORIES_ABBR.totalRedZoneAttemptsAgainst,
    longName: 'totalRedZoneAttemptsAgainst',
    label: 'Attempts',
    description: 'Red Zone Attempts Against',
    group: DefensiveGroup.RedZoneStand,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalRedZoneStops]: {
    abrv: STAT_CATEGORIES_ABBR.totalRedZoneStops,
    longName: 'totalRedZoneStops',
    label: 'Stops',
    description: 'Red Zone Stops',
    group: DefensiveGroup.RedZoneStand,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalRedZoneStopEfficiency]: {
    abrv: STAT_CATEGORIES_ABBR.totalRedZoneStopEfficiency,
    longName: 'totalRedZoneStopEfficiency',
    label: 'Efficiency',
    description: 'Red Zone Stop Efficiency',
    group: DefensiveGroup.RedZoneStand,
    kind: CategoryRankingsKind.Defense,
  },
  [STAT_CATEGORIES_ABBR.totalDrives]: {
    abrv: STAT_CATEGORIES_ABBR.totalDrives,
    longName: 'totalDrives',
    label: 'Drives',
    description: 'Total Drives',
    group: OffensiveGroup.Totals,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalPlays]: {
    abrv: STAT_CATEGORIES_ABBR.totalPlays,
    longName: 'totalPlays',
    label: 'Plays',
    description: 'Total Plays',
    group: OffensiveGroup.Totals,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalYards]: {
    abrv: STAT_CATEGORIES_ABBR.totalYards,
    longName: 'totalYards',
    label: 'Yards',
    description: 'Total Yards',
    group: OffensiveGroup.Totals,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalTimeOfPossession]: {
    abrv: STAT_CATEGORIES_ABBR.totalTimeOfPossession,
    longName: 'totalTimeOfPossession',
    label: 'ToP',
    description: 'Time of Possession',
    group: OffensiveGroup.Totals,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalYardsPerPlay]: {
    abrv: STAT_CATEGORIES_ABBR.totalYardsPerPlay,
    longName: 'totalYardsPerPlay',
    label: 'Yards Per Play',
    description: 'Yards Per Play',
    group: OffensiveGroup.Totals,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalPointsScored]: {
    abrv: STAT_CATEGORIES_ABBR.totalPointsScored,
    longName: 'totalPointsScored',
    label: 'Points Scored',
    description: 'Points Scored',
    group: OffensiveGroup.Scoring,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalScoringTotalTDFor]: {
    abrv: STAT_CATEGORIES_ABBR.totalScoringTotalTDFor,
    longName: 'totalScoringTotalTDFor',
    label: 'Total TDs',
    description: 'Scored Total TDs',
    group: OffensiveGroup.Scoring,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalScoringPassingTDFor]: {
    abrv: STAT_CATEGORIES_ABBR.totalScoringPassingTDFor,
    longName: 'totalScoringPassingTDFor',
    label: 'Pass TDs',
    description: 'Scored Passing TDs',
    group: OffensiveGroup.Scoring,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalScoringRushingTDFor]: {
    abrv: STAT_CATEGORIES_ABBR.totalScoringRushingTDFor,
    longName: 'totalScoringRushingTDFor',
    label: 'Rush TDs',
    description: 'Scored Rushing TDs',
    group: OffensiveGroup.Scoring,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalScoringFGFor]: {
    abrv: STAT_CATEGORIES_ABBR.totalScoringFGFor,
    longName: 'totalScoringFGFor',
    label: 'FGs',
    description: 'Scored Field Goals',
    group: OffensiveGroup.Scoring,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalScoringEPFor]: {
    abrv: STAT_CATEGORIES_ABBR.totalScoringEPFor,
    longName: 'totalScoringEPFor',
    label: 'PAT',
    description: 'Scored Extra Points',
    group: OffensiveGroup.Scoring,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalScoring2PtFor]: {
    abrv: STAT_CATEGORIES_ABBR.totalScoring2PtFor,
    longName: 'totalScoring2PtFor',
    label: '2pt',
    description: 'Scored 2pt Conversions',
    group: OffensiveGroup.Scoring,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalPassingYards]: {
    abrv: STAT_CATEGORIES_ABBR.totalPassingYards,
    longName: 'totalPassingYards',
    label: 'Yards',
    description: 'Passing Yards',
    group: OffensiveGroup.Passing,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalPassAttempts]: {
    abrv: STAT_CATEGORIES_ABBR.totalPassAttempts,
    longName: 'totalPassAttempts',
    label: 'Attempts',
    description: 'Pass Attempts',
    group: OffensiveGroup.Passing,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalPassCompletions]: {
    abrv: STAT_CATEGORIES_ABBR.totalPassCompletions,
    longName: 'totalPassCompletions',
    label: 'Completions',
    description: 'Pass Completions',
    group: OffensiveGroup.Passing,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalPassingPercentage]: {
    abrv: STAT_CATEGORIES_ABBR.totalPassingPercentage,
    longName: 'totalPassingPercentage',
    label: 'Percentage',
    description: 'Passing Completion Percentage',
    group: OffensiveGroup.Passing,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalPassingYardsPerAttempt]: {
    abrv: STAT_CATEGORIES_ABBR.totalPassingYardsPerAttempt,
    longName: 'totalPassingYardsPerAttempt',
    label: 'Yards Per Pass',
    description: 'Passing Yards Per Attempt',
    group: OffensiveGroup.Passing,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalRushingYards]: {
    abrv: STAT_CATEGORIES_ABBR.totalRushingYards,
    longName: 'totalRushingYards',
    label: 'Yards',
    description: 'Rushing Yards',
    group: OffensiveGroup.Rushing,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalRushingAttempts]: {
    abrv: STAT_CATEGORIES_ABBR.totalRushingAttempts,
    longName: 'totalRushingAttempts',
    label: 'Attempts',
    description: 'Rushing Attempts',
    group: OffensiveGroup.Rushing,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalRushingYardsPerAttempt]: {
    abrv: STAT_CATEGORIES_ABBR.totalRushingYardsPerAttempt,
    longName: 'totalRushingYardsPerAttempt',
    label: 'Yards per carry',
    description: 'Rushing Yards Per Attempt',
    group: OffensiveGroup.Rushing,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalSackedTimes]: {
    abrv: STAT_CATEGORIES_ABBR.totalSackedTimes,
    longName: 'totalSackedTimes',
    label: 'Times Sacked',
    description: 'Total Times Sacked',
    group: OffensiveGroup.NegativePlays,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalSackedYardsLost]: {
    abrv: STAT_CATEGORIES_ABBR.totalSackedYardsLost,
    longName: 'totalSackedYardsLost',
    label: 'Yards Lost',
    description: 'Sacked Yards Lost',
    group: OffensiveGroup.NegativePlays,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalPenalties]: {
    abrv: STAT_CATEGORIES_ABBR.totalPenalties,
    longName: 'totalPenalties',
    label: 'Penalties',
    description: 'Penalties',
    group: OffensiveGroup.NegativePlays,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalPenaltyYards]: {
    abrv: STAT_CATEGORIES_ABBR.totalPenaltyYards,
    longName: 'totalPenaltyYards',
    label: 'Penalty Yards',
    description: 'Penalty Yards',
    group: OffensiveGroup.NegativePlays,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalFumblesLost]: {
    abrv: STAT_CATEGORIES_ABBR.totalFumblesLost,
    longName: 'totalFumblesLost',
    label: 'Fumbles',
    description: 'Fumbles Lost',
    group: OffensiveGroup.Turnovers,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalInterceptionsThrown]: {
    abrv: STAT_CATEGORIES_ABBR.totalInterceptionsThrown,
    longName: 'totalInterceptionsThrown',
    label: 'INTs',
    description: 'Interceptions Thrown',
    group: OffensiveGroup.Turnovers,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalFirstDowns]: {
    abrv: STAT_CATEGORIES_ABBR.totalFirstDowns,
    longName: 'totalFirstDowns',
    label: 'Total 1st Downs',
    description: 'Total First Downs',
    group: OffensiveGroup.FirstDowns,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalFirstDownsFromPassing]: {
    abrv: STAT_CATEGORIES_ABBR.totalFirstDownsFromPassing,
    longName: 'totalFirstDownsFromPassing',
    label: '1st Downs by Pass',
    description: 'First Downs From Passing',
    group: OffensiveGroup.FirstDowns,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalFirstDownsFromRushing]: {
    abrv: STAT_CATEGORIES_ABBR.totalFirstDownsFromRushing,
    longName: 'totalFirstDownsFromRushing',
    label: '1st Downs by Rush',
    description: 'First Downs From Rushing',
    group: OffensiveGroup.FirstDowns,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalFirstDownsFromPenalties]: {
    abrv: STAT_CATEGORIES_ABBR.totalFirstDownsFromPenalties,
    longName: 'totalFirstDownsFromPenalties',
    label: '1st Downs by Penalty',
    description: 'First Downs From Penalties',
    group: OffensiveGroup.FirstDowns,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalThirdDownAttempts]: {
    abrv: STAT_CATEGORIES_ABBR.totalThirdDownAttempts,
    longName: 'totalThirdDownAttempts',
    label: '3rd Down Attempts',
    description: 'Third Down Attempts',
    group: OffensiveGroup.ThirdDowns,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalThirdDownConverted]: {
    abrv: STAT_CATEGORIES_ABBR.totalThirdDownConverted,
    longName: 'totalThirdDownConverted',
    label: '3rd Down Converted',
    description: 'Third Down Converted',
    group: OffensiveGroup.ThirdDowns,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalThirdDownEfficiency]: {
    abrv: STAT_CATEGORIES_ABBR.totalThirdDownEfficiency,
    longName: 'totalThirdDownEfficiency',
    label: '3rd Down Efficiency',
    description: 'Third Down Efficiency',
    group: OffensiveGroup.ThirdDowns,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalFourthDownAttempts]: {
    abrv: STAT_CATEGORIES_ABBR.totalFourthDownAttempts,
    longName: 'totalFourthDownAttempts',
    label: '4th Down Attempts',
    description: 'Fourth Down Attempts',
    group: OffensiveGroup.FourthDowns,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalFourthDownScored]: {
    abrv: STAT_CATEGORIES_ABBR.totalFourthDownScored,
    longName: 'totalFourthDownScored',
    label: '4th Down Converted',
    description: 'Fourth Down Converted',
    group: OffensiveGroup.FourthDowns,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalFourthDownEfficiency]: {
    abrv: STAT_CATEGORIES_ABBR.totalFourthDownEfficiency,
    longName: 'totalFourthDownEfficiency',
    label: '4th Down Efficiency',
    description: 'Fourth Down Efficiency',
    group: OffensiveGroup.FourthDowns,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalRedZoneAttempts]: {
    abrv: STAT_CATEGORIES_ABBR.totalRedZoneAttempts,
    longName: 'totalRedZoneAttempts',
    label: 'RZ Attempts',
    description: 'Red Zone Attempts',
    group: OffensiveGroup.RedZone,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalRedZoneScored]: {
    abrv: STAT_CATEGORIES_ABBR.totalRedZoneScored,
    longName: 'totalRedZoneScored',
    label: 'RZ Scored',
    description: 'Red Zone Scored',
    group: OffensiveGroup.RedZone,
    kind: CategoryRankingsKind.Offense,
  },
  [STAT_CATEGORIES_ABBR.totalRedZoneEfficiency]: {
    abrv: STAT_CATEGORIES_ABBR.totalRedZoneEfficiency,
    longName: 'totalRedZoneEfficiency',
    label: 'RZ Efficiency',
    description: 'Red Zone Efficiency',
    group: OffensiveGroup.RedZone,
    kind: CategoryRankingsKind.Offense,
  },
};

export const GroupDefinitions: Record<CategoryGroupName, GroupDefinition> = {
  [DefensiveGroup.ScoringAllowed]: {
    id: DefensiveGroup.ScoringAllowed,
    label: 'Scoring Allowed',
    kind: CategoryRankingsKind.Defense,
    categories: [
      STAT_CATEGORIES_ABBR.totalPointsAllowed,
      STAT_CATEGORIES_ABBR.totalScoringTotalTDAgainst,
      STAT_CATEGORIES_ABBR.totalScoringPassingTDAgainst,
      STAT_CATEGORIES_ABBR.totalScoringRushingTDAgainst,
      STAT_CATEGORIES_ABBR.totalScoringFGAgainst,
      STAT_CATEGORIES_ABBR.totalScoring2PtAgainst,
      STAT_CATEGORIES_ABBR.totalScoringEPAgainst,
    ],
  },
  [DefensiveGroup.YardsAllowed]: {
    id: DefensiveGroup.YardsAllowed,
    label: 'Yards Allowed',
    kind: CategoryRankingsKind.Defense,
    categories: [
      STAT_CATEGORIES_ABBR.totalYardsAllowed,
      STAT_CATEGORIES_ABBR.totalPassingYardsAllowed,
      STAT_CATEGORIES_ABBR.totalRushingYardsAllowed,
    ],
  },
  [DefensiveGroup.VsPass]: {
    id: DefensiveGroup.VsPass,
    label: 'vs Pass',
    kind: CategoryRankingsKind.Defense,
    categories: [
      STAT_CATEGORIES_ABBR.totalPassAttemptsAgainst,
      STAT_CATEGORIES_ABBR.totalPassIncompletions,
      STAT_CATEGORIES_ABBR.totalPassIncompletePercentage,
    ],
  },
  [DefensiveGroup.PositivePlays]: {
    id: DefensiveGroup.PositivePlays,
    label: 'Positive Plays',
    kind: CategoryRankingsKind.Defense,
    categories: [
      STAT_CATEGORIES_ABBR.totalForcedFumbles,
      STAT_CATEGORIES_ABBR.totalFumblesRecovered,
      STAT_CATEGORIES_ABBR.totalDefensiveInterceptions,
      STAT_CATEGORIES_ABBR.totalSacks,
      STAT_CATEGORIES_ABBR.totalSafeties,
      STAT_CATEGORIES_ABBR.totalDefensiveOrSpecialTeamsTds,
    ],
  },
  [DefensiveGroup.ThirdDownStand]: {
    id: DefensiveGroup.ThirdDownStand,
    label: '3rd Downs',
    kind: CategoryRankingsKind.Defense,
    categories: [
      STAT_CATEGORIES_ABBR.totalThirdDownAttemptsAgainst,
      STAT_CATEGORIES_ABBR.totalThirdDownStops,
      STAT_CATEGORIES_ABBR.totalThirdDownStopEfficiency,
    ],
  },
  [DefensiveGroup.FourthDownStand]: {
    id: DefensiveGroup.FourthDownStand,
    label: '4th Downs',
    kind: CategoryRankingsKind.Defense,
    categories: [
      STAT_CATEGORIES_ABBR.totalFourthDownAttemptsAgainst,
      STAT_CATEGORIES_ABBR.totalFourthDownStops,
      STAT_CATEGORIES_ABBR.totalFourthDownStopEfficiency,
    ],
  },
  [DefensiveGroup.RedZoneStand]: {
    id: DefensiveGroup.RedZoneStand,
    label: 'Red Zone',
    kind: CategoryRankingsKind.Defense,
    categories: [
      STAT_CATEGORIES_ABBR.totalRedZoneAttemptsAgainst,
      STAT_CATEGORIES_ABBR.totalRedZoneStops,
      STAT_CATEGORIES_ABBR.totalRedZoneStopEfficiency,
    ],
  },
  [OffensiveGroup.Totals]: {
    id: OffensiveGroup.Totals,
    label: 'Totals',
    kind: CategoryRankingsKind.Offense,
    categories: [
      STAT_CATEGORIES_ABBR.totalDrives,
      STAT_CATEGORIES_ABBR.totalPlays,
      STAT_CATEGORIES_ABBR.totalYards,
      STAT_CATEGORIES_ABBR.totalTimeOfPossession,
      STAT_CATEGORIES_ABBR.totalYardsPerPlay,
    ],
  },
  [OffensiveGroup.Scoring]: {
    id: OffensiveGroup.Scoring,
    label: 'Scoring',
    kind: CategoryRankingsKind.Offense,
    categories: [
      STAT_CATEGORIES_ABBR.totalPointsScored,
      STAT_CATEGORIES_ABBR.totalScoringTotalTDFor,
      STAT_CATEGORIES_ABBR.totalScoringPassingTDFor,
      STAT_CATEGORIES_ABBR.totalScoringRushingTDFor,
      STAT_CATEGORIES_ABBR.totalScoringFGFor,
      STAT_CATEGORIES_ABBR.totalScoringEPFor,
      STAT_CATEGORIES_ABBR.totalScoring2PtFor,
    ],
  },
  [OffensiveGroup.Passing]: {
    id: OffensiveGroup.Passing,
    label: 'Passing',
    kind: CategoryRankingsKind.Offense,
    categories: [
      STAT_CATEGORIES_ABBR.totalPassingYards,
      STAT_CATEGORIES_ABBR.totalPassAttempts,
      STAT_CATEGORIES_ABBR.totalPassCompletions,
      STAT_CATEGORIES_ABBR.totalPassingPercentage,
      STAT_CATEGORIES_ABBR.totalPassingYardsPerAttempt,
    ],
  },
  [OffensiveGroup.Rushing]: {
    id: OffensiveGroup.Rushing,
    label: 'Rushing',
    kind: CategoryRankingsKind.Offense,
    categories: [
      STAT_CATEGORIES_ABBR.totalRushingYards,
      STAT_CATEGORIES_ABBR.totalRushingAttempts,
      STAT_CATEGORIES_ABBR.totalRushingYardsPerAttempt,
    ],
  },
  [OffensiveGroup.NegativePlays]: {
    id: OffensiveGroup.NegativePlays,
    label: 'Negative Plays',
    kind: CategoryRankingsKind.Offense,
    categories: [
      STAT_CATEGORIES_ABBR.totalSackedTimes,
      STAT_CATEGORIES_ABBR.totalSackedYardsLost,
      STAT_CATEGORIES_ABBR.totalPenalties,
      STAT_CATEGORIES_ABBR.totalPenaltyYards,
    ],
  },
  [OffensiveGroup.Turnovers]: {
    id: OffensiveGroup.Turnovers,
    label: 'Turnovers',
    kind: CategoryRankingsKind.Offense,
    categories: [STAT_CATEGORIES_ABBR.totalFumblesLost, STAT_CATEGORIES_ABBR.totalInterceptionsThrown],
  },
  [OffensiveGroup.FirstDowns]: {
    id: OffensiveGroup.FirstDowns,
    label: '1st Downs',
    kind: CategoryRankingsKind.Offense,
    categories: [
      STAT_CATEGORIES_ABBR.totalFirstDowns,
      STAT_CATEGORIES_ABBR.totalFirstDownsFromPassing,
      STAT_CATEGORIES_ABBR.totalFirstDownsFromRushing,
      STAT_CATEGORIES_ABBR.totalFirstDownsFromPenalties,
    ],
  },
  [OffensiveGroup.ThirdDowns]: {
    id: OffensiveGroup.ThirdDowns,
    label: '3rd Downs',
    kind: CategoryRankingsKind.Offense,
    categories: [
      STAT_CATEGORIES_ABBR.totalThirdDownAttempts,
      STAT_CATEGORIES_ABBR.totalThirdDownConverted,
      STAT_CATEGORIES_ABBR.totalThirdDownEfficiency,
    ],
  },
  [OffensiveGroup.FourthDowns]: {
    id: OffensiveGroup.FourthDowns,
    label: '4th Downs',
    kind: CategoryRankingsKind.Offense,
    categories: [
      STAT_CATEGORIES_ABBR.totalFourthDownAttempts,
      STAT_CATEGORIES_ABBR.totalFourthDownScored,
      STAT_CATEGORIES_ABBR.totalFourthDownEfficiency,
    ],
  },
  [OffensiveGroup.RedZone]: {
    id: OffensiveGroup.RedZone,
    label: 'Red Zone',
    kind: CategoryRankingsKind.Offense,
    categories: [
      STAT_CATEGORIES_ABBR.totalRedZoneAttempts,
      STAT_CATEGORIES_ABBR.totalRedZoneScored,
      STAT_CATEGORIES_ABBR.totalRedZoneEfficiency,
    ],
  },
};

export const CategoryLabelMap: Record<string, CategoryDefinition> = Object.values(CategoryDefinitions).reduce(
  (acc, def) => {
    acc[def.label] = def;
    return acc;
  },
  {} as Record<string, CategoryDefinition>
);

export const GroupsByKind: Record<CategoryRankingsKindType, GroupDefinition[]> = {
  [CategoryRankingsKind.Defense]: [
    GroupDefinitions[DefensiveGroup.ScoringAllowed],
    GroupDefinitions[DefensiveGroup.YardsAllowed],
    GroupDefinitions[DefensiveGroup.VsPass],
    GroupDefinitions[DefensiveGroup.PositivePlays],
    GroupDefinitions[DefensiveGroup.ThirdDownStand],
    GroupDefinitions[DefensiveGroup.FourthDownStand],
    GroupDefinitions[DefensiveGroup.RedZoneStand],
  ],
  [CategoryRankingsKind.Offense]: [
    GroupDefinitions[OffensiveGroup.Totals],
    GroupDefinitions[OffensiveGroup.Scoring],
    GroupDefinitions[OffensiveGroup.Passing],
    GroupDefinitions[OffensiveGroup.Rushing],
    GroupDefinitions[OffensiveGroup.NegativePlays],
    GroupDefinitions[OffensiveGroup.Turnovers],
    GroupDefinitions[OffensiveGroup.FirstDowns],
    GroupDefinitions[OffensiveGroup.ThirdDowns],
    GroupDefinitions[OffensiveGroup.FourthDowns],
    GroupDefinitions[OffensiveGroup.RedZone],
  ],
};
